import { useEffect, useRef, useState } from "react"
import { useStoreState } from "easy-peasy"
import { useSearchParams } from "react-router-dom"
import api from "./api"

const BOOKMARKS_KEY = "rpt_bookmarks"

const DOG_REPORT_PREFIXES = ["dog_"]
const FARM_REPORT_PREFIXES = ["farm_"]

function reportCategory(reportId) {
	if (DOG_REPORT_PREFIXES.some((p) => reportId.startsWith(p))) return "dog"
	if (FARM_REPORT_PREFIXES.some((p) => reportId.startsWith(p))) return "farm"
	return "other"
}

const SYSTEM_PARAMS = ["client_id", "deputy_id"]
const DEPUTY_SELECTABLE = ["dog_deputy_daily_tags"]
const KENNEL_NAME_REPORTS = ["dog_kennel_certificate"]

const Reports = () => {
	const Token = useStoreState((state) => state.Token)
	const [searchParams] = useSearchParams()
	const category = searchParams.get("category") || ""
	const [reports, setReports] = useState([])
	const [loading, setLoading] = useState(true)
	const [fetchError, setFetchError] = useState("")
	const [search, setSearch] = useState("")
	const [bookmarks, setBookmarks] = useState(() => {
		try {
			return JSON.parse(localStorage.getItem(BOOKMARKS_KEY)) || []
		} catch {
			return []
		}
	})
	const [selected, setSelected] = useState(null)
	const [params, setParams] = useState({})
	const [iframeContent, setIframeContent] = useState("")
	const [running, setRunning] = useState(false)
	const [runError, setRunError] = useState("")
	const [deputies, setDeputies] = useState([])
	const [kennelNames, setKennelNames] = useState([])
	const [kennelDropOpen, setKennelDropOpen] = useState(false)
	const iframeRef = useRef(null)

	useEffect(() => {
		api.get("/reports/")
			.then((res) => {
				setReports(res.data.reports)
				setLoading(false)
			})
			.catch(() => {
				setFetchError("Failed to load reports.")
				setLoading(false)
			})
	}, [])

	useEffect(() => {
		setSelected(null)
		setIframeContent("")
		setRunError("")
		setParams({})
	}, [category])

	// Bookmarked reports float to the top, rest sorted alphabetically
	const sortedReports = [...reports].sort((a, b) => {
		const aB = bookmarks.includes(a.report_id)
		const bB = bookmarks.includes(b.report_id)
		if (aB && !bB) return -1
		if (!aB && bB) return 1
		return a.display_name.localeCompare(b.display_name)
	})

	const filteredReports = sortedReports.filter((r) => {
		if (category && reportCategory(r.report_id) !== category) return false
		return r.display_name.toLowerCase().includes(search.toLowerCase())
	})

	function toggleBookmark(reportId, e) {
		e.stopPropagation()
		const updated = bookmarks.includes(reportId)
			? bookmarks.filter((id) => id !== reportId)
			: [...bookmarks, reportId]
		setBookmarks(updated)
		localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(updated))
	}

	function selectReport(report) {
		setSelected(report)
		setIframeContent("")
		setRunError("")
		setDeputies([])
		setKennelNames([])
		setKennelDropOpen(false)
		const now = new Date()
		const initial = {}
		for (const p of report.parameters) {
			if (p.name === "tax_year" || p.name === "year" || p.name === "fiscal_year") {
				initial[p.name] = String(now.getFullYear())
			} else if (p.name === "issued_month") {
				initial[p.name] = String(now.getMonth() + 1)
			} else if (p.name === "date_from") {
				const yyyy = now.getFullYear()
				const mm = String(now.getMonth() + 1).padStart(2, "0")
				initial[p.name] = `${yyyy}-${mm}-01`
			} else if (p.name === "date_to") {
				const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
				const yyyy = lastDay.getFullYear()
				const mm = String(lastDay.getMonth() + 1).padStart(2, "0")
				const dd = String(lastDay.getDate()).padStart(2, "0")
				initial[p.name] = `${yyyy}-${mm}-${dd}`
			} else if (p.type === "date") {
				const yyyy = now.getFullYear()
				const mm = String(now.getMonth() + 1).padStart(2, "0")
				const dd = String(now.getDate()).padStart(2, "0")
				initial[p.name] = `${yyyy}-${mm}-${dd}`
			} else if (p.name === "deputy_id" && DEPUTY_SELECTABLE.includes(report.report_id)) {
				initial[p.name] = Token?.login_id ? String(Token.login_id) : ""
			} else if (p.default !== null && p.default !== undefined) {
				initial[p.name] = String(p.default)
			} else {
				initial[p.name] = ""
			}
		}
		setParams(initial)

		if (DEPUTY_SELECTABLE.includes(report.report_id)) {
			api.post("/routes/client_users", {
				login_id: Token.login_id,
				client_id: Token.client_id,
			}).then((res) => setDeputies(res.data)).catch(() => setDeputies([]))
		}

		if (KENNEL_NAME_REPORTS.includes(report.report_id)) {
			api.get(`/routes/kennel_names?client_id=${Token.client_id}`)
				.then((res) => setKennelNames(res.data))
				.catch(() => setKennelNames([]))
		}
	}

	function handleParamChange(name, value) {
		setParams((prev) => ({ ...prev, [name]: value }))
	}

	function canRun() {
		if (!selected) return false
		for (const p of selected.parameters) {
			if (p.required && !SYSTEM_PARAMS.includes(p.name) && !params[p.name]) return false
		}
		return true
	}

	async function runReport() {
		setRunning(true)
		setRunError("")
		setIframeContent("")
		try {
			const queryParams = new URLSearchParams()
			for (const [k, v] of Object.entries(params)) {
				if (v !== "") queryParams.set(k, v)
			}
			if (Token?.client_id) queryParams.set("client_id", Token.client_id)
			if (Token?.login_id && !queryParams.get("deputy_id")) queryParams.set("deputy_id", Token.login_id)
			const url = `/reports/${selected.report_id}/preview${queryParams.toString() ? "?" + queryParams.toString() : ""}`
			const res = await api.get(url, { responseType: "text" })
			setIframeContent(res.data)
		} catch (err) {
			const detail = err.response?.data?.detail || "Failed to run report."
			setRunError(typeof detail === "string" ? detail : JSON.stringify(detail))
		} finally {
			setRunning(false)
		}
	}

	function handlePrint() {
		if (iframeRef.current?.contentWindow) {
			iframeRef.current.contentWindow.print()
		}
	}

	const MONTHS = [
		"January", "February", "March", "April", "May", "June",
		"July", "August", "September", "October", "November", "December",
	]

	function renderParamInput(param) {
		const val = params[param.name] ?? ""
		const id = `rpt-param-${param.name}`
		if (param.name === "deputy_id" && deputies.length > 0) {
			return (
				<select
					id={id}
					value={val}
					onChange={(e) => handleParamChange(param.name, e.target.value)}
				>
					{deputies.map((d) => (
						<option key={d[0]} value={d[0]}>
							{d[2]}, {d[3]}
						</option>
					))}
				</select>
			)
		}
		if (param.name === "issued_month") {
			return (
				<select
					id={id}
					value={val}
					onChange={(e) => handleParamChange(param.name, e.target.value)}
				>
					{MONTHS.map((name, i) => (
						<option key={i + 1} value={i + 1}>{name}</option>
					))}
				</select>
			)
		}
		if (param.options && param.options.length > 0) {
			return (
				<select
					id={id}
					value={val}
					onChange={(e) => handleParamChange(param.name, e.target.value)}
				>
					{param.options.map((opt) => (
						<option key={opt.value} value={opt.value}>{opt.label}</option>
					))}
				</select>
			)
		}
		if (param.name === "kennel_name" && KENNEL_NAME_REPORTS.includes(selected?.report_id)) {
			const filtered = kennelNames.filter((n) =>
				n.toLowerCase().includes(val.toLowerCase())
			)
			return (
				<div style={{ position: "relative" }}>
					<input
						id={id}
						type="text"
						value={val}
						autoComplete="off"
						placeholder="Type to search kennels..."
						onChange={(e) => {
							handleParamChange(param.name, e.target.value)
							setKennelDropOpen(true)
						}}
						onFocus={() => setKennelDropOpen(true)}
						onBlur={() => setTimeout(() => setKennelDropOpen(false), 150)}
					/>
					{kennelDropOpen && filtered.length > 0 && (
						<ul style={{
							position: "absolute",
							top: "100%",
							left: 0,
							right: 0,
							background: "#fff",
							border: "1px solid #ccc",
							borderRadius: "4px",
							listStyle: "none",
							margin: 0,
							padding: 0,
							zIndex: 100,
							maxHeight: "200px",
							overflowY: "auto",
							boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
							fontSize: "0.8rem",
						}}>
							{filtered.map((name) => (
								<li
									key={name}
									style={{ padding: "4px 8px", cursor: "pointer", color: "#111" }}
									onMouseEnter={(e) => e.currentTarget.style.background = "#f0f4ff"}
									onMouseLeave={(e) => e.currentTarget.style.background = ""}
									onMouseDown={() => {
										handleParamChange(param.name, name)
										setKennelDropOpen(false)
									}}
								>
									{name}
								</li>
							))}
						</ul>
					)}
				</div>
			)
		}

		switch (param.type) {
			case "int":
				return (
					<input
						id={id}
						type="number"
						value={val}
						onChange={(e) => handleParamChange(param.name, e.target.value)}
					/>
				)
			case "date":
				return (
					<input
						id={id}
						type="date"
						value={val}
						onChange={(e) => handleParamChange(param.name, e.target.value)}
					/>
				)
			case "bool":
				return (
					<input
						id={id}
						type="checkbox"
						checked={val === "true"}
						onChange={(e) =>
							handleParamChange(param.name, e.target.checked ? "true" : "false")
						}
					/>
				)
			default:
				return (
					<input
						id={id}
						type="text"
						value={val}
						onChange={(e) => handleParamChange(param.name, e.target.value)}
					/>
				)
		}
	}

	return (
		<main className="Main">
			<div className="rpt-container">

				{/* ── Sidebar ─────────────────────────────── */}
				<div className="rpt-sidebar">
					{category && (
						<div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.6)", marginBottom: "4px", textTransform: "capitalize" }}>
							{category} Reports
						</div>
					)}
					<input
						className="rpt-search"
						type="text"
						placeholder="Search reports..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					<div className="rpt-list">
						{loading && <p className="rpt-status">Loading...</p>}
						{fetchError && <p className="rpt-status rpt-error">{fetchError}</p>}
						{!loading &&
							!fetchError &&
							filteredReports.map((report) => (
								<div
									key={report.report_id}
									className={`rpt-btn${selected?.report_id === report.report_id ? " rpt-btn-active" : ""}`}
									onClick={() => selectReport(report)}
									title={report.description}
								>
									<span className="rpt-btn-name">{report.display_name}</span>
									<span
										className={`rpt-bookmark${bookmarks.includes(report.report_id) ? " rpt-bookmarked" : ""}`}
										onClick={(e) => toggleBookmark(report.report_id, e)}
										title={
											bookmarks.includes(report.report_id)
												? "Remove bookmark"
												: "Bookmark"
										}
									>
										{bookmarks.includes(report.report_id) ? "★" : "☆"}
									</span>
								</div>
							))}
						{!loading && !fetchError && filteredReports.length === 0 && (
							<p className="rpt-status">No reports found.</p>
						)}
					</div>
				</div>

				{/* ── Main area ───────────────────────────── */}
				<div className="rpt-main">
					{!selected ? (
						<div className="rpt-placeholder">
							<p>Select a report</p>
						</div>
					) : (
						<>
							{/* Config panel */}
							<div className="rpt-config-panel">
								<div className="rpt-config-header">
									<h2 className="rpt-title">{selected.display_name}</h2>
									{iframeContent && (
										<button
											className="rpt-print-btn"
											onClick={handlePrint}
											title="Print / Save as PDF"
										>
											Print / PDF
										</button>
									)}
								</div>

								{selected.parameters.filter(p => {
										const hidden = DEPUTY_SELECTABLE.includes(selected.report_id)
											? SYSTEM_PARAMS.filter(n => n !== "deputy_id")
											: SYSTEM_PARAMS
										return !hidden.includes(p.name)
									}).length > 0 && (
									<div className="rpt-params">
										{selected.parameters.filter(p => {
											const hidden = DEPUTY_SELECTABLE.includes(selected.report_id)
												? SYSTEM_PARAMS.filter(n => n !== "deputy_id")
												: SYSTEM_PARAMS
											return !hidden.includes(p.name)
										}).map((param) => (
											<div key={param.name} className={`rpt-param-row${param.type === "bool" ? " rpt-param-row--bool" : ""}`}>
												<label htmlFor={`rpt-param-${param.name}`}>
													{param.label}
													{param.required && (
														<span className="rpt-required"> *</span>
													)}
												</label>
												{renderParamInput(param)}
											</div>
										))}
									</div>
								)}

								<div className="rpt-actions">
									<button
										className="saveBtn"
										onClick={runReport}
										disabled={!canRun() || running}
									>
										{running ? "Running..." : "Run Report"}
									</button>
									{runError && (
										<span className="rpt-run-error">{runError}</span>
									)}
								</div>
							</div>

							{/* Output iframe */}
							{iframeContent && (
								<div className="rpt-output">
									<iframe
										ref={iframeRef}
										className="rpt-iframe"
										srcDoc={iframeContent}
										title={selected.display_name}
									/>
								</div>
							)}
						</>
					)}
				</div>

			</div>
		</main>
	)
}

export default Reports
